import { AdderType, AdderIncrementer, AdderGallery } from '@modules/husleie-framework/src/types/Adder';
import FileUpload from '@modules/husleie-framework/src/types/FileUpload.entity';
import Keychain from '@modules/husleie-framework/src/types/Keychain.entity';
import type { RentalObject } from '@modules/husleie-framework/src/types/RentalObject.entity';
import { RentalObjectFurnishingStandard } from '@modules/husleie-framework/src/types/RentalObject.entity';
import { RentalObjectDetails } from '@modules/husleie-framework/src/types/RentalObjectDetails.entity';
import { ImageSlide } from '@modules/husleie-framework/src/types/Swiper.entity';
import { Utility } from '@modules/husleie-framework/src/types/Utility';
import { RentalObjectGalleryUpdate } from '@modules/store/rentalObject/types/RentalObjectGalleryUpdate.type';
import type RentalObjectState from '@modules/store/rentalObject/types/RentalObjectState.entity';
import { merge, cloneDeep } from 'lodash';
import Vue from 'vue';
import { MutationTree } from 'vuex';

export const mutations: MutationTree<RentalObjectState> = {
  setLoading: (state, payload: boolean) => {
    state.loading = payload;
  },
  setRentalObject: (state, payload: RentalObject) => {
    state.rentalObject = cloneDeep(merge(state.rentalObject, payload));
    state.loaded = true;
  },

  setRentalObjectRoom: (state: RentalObjectState, payload: { index: number; room: AdderType }) => {
    Vue.set(state.rentalObject.rooms!, payload.index, payload.room);
  },
  addRentalObjectRoom: (state: RentalObjectState, payload: AdderType) => {
    const list: AdderType[] = state.rentalObject.rooms ? [...state.rentalObject.rooms] : [];
    list.push(payload);
    Vue.set(state.rentalObject, 'rooms', list);
  },
  incrementRentalObjectRoomCount: (state: RentalObjectState, index: number) => {
    const room: AdderIncrementer = state.rentalObject.rooms![index] as AdderIncrementer;
    if (!room.quantity) {
      room.quantity = 0;
    }
    room.quantity++;
    Vue.set(state.rentalObject.rooms!, index, room);
  },
  decrementRentalObjectRoomCount: (state: RentalObjectState, index: number) => {
    const room: AdderIncrementer = state.rentalObject.rooms![index] as AdderIncrementer;
    if (!room.quantity) {
      room.quantity = 0;
    } else if (room.quantity > 0) {
      room.quantity--;
    }
    Vue.set(state.rentalObject.rooms!, index, room);
  },
  deleteRentalObjectRoom: (state: RentalObjectState, index: number) => {
    state.rentalObject.rooms!.splice(index, 1);
  },

  addRentalObjectFurniture: (state: RentalObjectState, payload: AdderType) => {
    const list: AdderType[] = state.rentalObject.furniture ? [...state.rentalObject.furniture] : [];
    list.push(payload);
    Vue.set(state.rentalObject, 'furniture', list);
  },
  incrementRentalObjectFurniture: (state: RentalObjectState, index: number) => {
    const furniture: AdderIncrementer = state.rentalObject.furniture![index] as AdderIncrementer;
    if (!furniture.quantity) {
      furniture.quantity = 0;
    }
    furniture.quantity++;
    Vue.set(state.rentalObject.furniture!, index, furniture);
  },
  decrementRentalObjectFurniture: (state: RentalObjectState, index: number) => {
    const furniture: AdderIncrementer = state.rentalObject.furniture![index] as AdderIncrementer;
    if (!furniture.quantity) {
      furniture.quantity = 0;
    } else if (furniture.quantity > 0) {
      furniture.quantity--;
    }
    Vue.set(state.rentalObject.furniture!, index, furniture);
  },
  deleteRentalObjectFurniture: (state: RentalObjectState, index: number) => {
    state.rentalObject.furniture!.splice(index, 1);
  },

  addRentalObjectStorageDetail: (state: RentalObjectState, payload: AdderType) => {
    const list: AdderType[] = state.rentalObject.storageDetail ? [...state.rentalObject.storageDetail] : [];
    list.push(payload);
    Vue.set(state.rentalObject, 'storageDetail', list);
  },
  incrementRentalObjectStorageDetail: (state: RentalObjectState, index: number) => {
    const storageDetail: AdderIncrementer = state.rentalObject.storageDetail![index] as AdderIncrementer;
    if (!storageDetail.quantity) {
      storageDetail.quantity = 0;
    }
    storageDetail.quantity++;
    Vue.set(state.rentalObject.storageDetail!, index, storageDetail);
  },
  decrementRentalObjectStorageDetail: (state: RentalObjectState, index: number) => {
    const storageDetail: AdderIncrementer = state.rentalObject.storageDetail![index] as AdderIncrementer;
    if (!storageDetail.quantity) {
      storageDetail.quantity = 0;
    } else if (storageDetail.quantity > 0) {
      storageDetail.quantity--;
    }
    Vue.set(state.rentalObject.storageDetail!, index, storageDetail);
  },
  deleteRentalObjectStorageDetail: (state: RentalObjectState, index: number) => {
    state.rentalObject.storageDetail!.splice(index, 1);
  },

  setRentalObjectKeyChain: (state: RentalObjectState, payload: Keychain) => {
    Vue.set(state.rentalObject, 'keychain', payload);
  },
  addRentalObjectKeychainKey: (state: RentalObjectState, payload: AdderType) => {
    const keychain: Keychain = state.rentalObject.keychain ? state.rentalObject.keychain : { keys: [] };
    keychain.keys.push(payload);
    Vue.set(state.rentalObject, 'keychain', keychain);
  },
  incrementRentalObjectKeychainKey: (state: RentalObjectState, index: number) => {
    const keychain: Keychain = state.rentalObject.keychain ? state.rentalObject.keychain : { keys: [] };
    const key: AdderIncrementer = keychain.keys[index] as AdderIncrementer;
    if (!key.quantity) {
      key.quantity = 0;
    }
    key.quantity++;
    Vue.set(state.rentalObject.keychain!.keys!, index, key);
  },
  decrementRentalObjectKeychainKey: (state: RentalObjectState, index: number) => {
    const keychain: Keychain = state.rentalObject.keychain ? state.rentalObject.keychain : { keys: [] };
    const key: AdderIncrementer = keychain.keys[index] as AdderIncrementer;
    if (!key.quantity) {
      key.quantity = 0;
    } else if (key.quantity > 0) {
      key.quantity--;
    }
    Vue.set(state.rentalObject.keychain!.keys!, index, key);
  },
  deleteRentalObjectKeychainKey: (state: RentalObjectState, index: number) => {
    state.rentalObject.keychain!.keys!.splice(index, 1);
  },
  addRentalObjectKeychainImages: (state: RentalObjectState, payload: ImageSlide[]) => {
    const images: ImageSlide[] = state.rentalObject.keychain?.images ? [...state.rentalObject.keychain.images] : [];
    if (!state.rentalObject.keychain) {
      Vue.set(state.rentalObject, 'keychain', { keys: [], images: [] });
    }
    Vue.set(state.rentalObject.keychain!, 'images', images.concat(payload));
  },
  deleteRentalObjectKeychainImages: (state: RentalObjectState, payload: ImageSlide[]) => {
    let images: ImageSlide[] = state.rentalObject.keychain?.images ? [...state.rentalObject.keychain.images] : [];
    images = images.filter(image => !payload.some(imageToDelete => imageToDelete.imageSrc === image.imageSrc));
    if (!state.rentalObject.keychain) {
      Vue.set(state.rentalObject, 'keychain', { keys: [], images: [] });
    }
    Vue.set(state.rentalObject.keychain!, 'images', images);
  },

  setRentalObjectUtilities: (state: RentalObjectState, payload: Utility[]) => {
    Vue.set(state.rentalObject, 'utilities', payload);
  },
  addRentalObjectUtility: (state: RentalObjectState, payload: Utility) => {
    const list: Utility[] = state.rentalObject.utilities ? [...state.rentalObject.utilities] : [];
    const index: number = list.findIndex(utility => utility.type === payload.type);
    if (index < 0) {
      list.push(payload);
      Vue.set(state.rentalObject, 'utilities', list);
    } else {
      Vue.set(state.rentalObject.utilities!, index, payload);
    }
  },
  setRentalObjectUtility: (state: RentalObjectState, payload: { index: number; utility: Utility }) => {
    Vue.set(state.rentalObject.utilities!, payload.index, payload.utility);
  },
  addRentalObjectUtilityImages: (state: RentalObjectState, payload: { index: number; images: ImageSlide[] }) => {
    const utility: Utility = state.rentalObject.utilities![payload.index];
    const images: ImageSlide[] = utility.meter!.reading!.images ? utility.meter!.reading!.images : [];
    utility.meter!.reading!.images = images.concat(payload.images);
    Vue.set(state.rentalObject.utilities!, payload.index, { ...utility });
  },
  deleteRentalObjectUtilityImages: (state: RentalObjectState, payload: { index: number; images: ImageSlide[] }) => {
    const utility: Utility = state.rentalObject.utilities![payload.index];
    if (utility?.meter?.reading?.images) {
      utility.meter.reading.images = utility.meter.reading.images.filter(
        image => !payload.images.some(imageToDelete => imageToDelete.imageSrc === image.imageSrc)
      );
    }
    Vue.set(state.rentalObject.utilities!, payload.index, { ...utility });
  },

  addRentalObjectImages: (state: RentalObjectState, payload: RentalObjectGalleryUpdate) => {
    const field: any = (state.rentalObject as any)[payload.fieldName];
    const gallery: AdderGallery = field[payload.index] as AdderGallery;
    const images: ImageSlide[] = gallery.images ? gallery.images : [];
    gallery.images = images.concat(payload.images);
    Vue.set((state.rentalObject as any)[payload.fieldName], payload.index, gallery);
  },
  deleteRentalObjectImages: (state: RentalObjectState, payload: RentalObjectGalleryUpdate) => {
    const field: any = (state.rentalObject as any)[payload.fieldName];
    const gallery: AdderGallery = field[payload.index] as AdderGallery;
    if (gallery.images) {
      gallery.images = gallery.images.filter(
        image => !payload.images.some(imageToDelete => imageToDelete.imageSrc === image.imageSrc)
      );
      Vue.set((state.rentalObject as any)[payload.fieldName], payload.index, gallery);
    }
  },

  setRentalObjectDetails: (state, payload: RentalObjectDetails) => {
    if (payload.furnishingStandard) {
      state.rentalObject.furnishingStandard = payload.furnishingStandard;
    }
    state.rentalObject.details = { ...payload, furnishingStandard: undefined };
  },

  setRentalObjectFurnishingStandard: (state: RentalObjectState, payload: RentalObjectFurnishingStandard) => {
    state.rentalObject.furnishingStandard = payload;
  },
  setRentalObjectParkingFacilities: (state: RentalObjectState, payload: boolean) => {
    state.rentalObject.parkingFacilities = payload;
  },

  setDefaultHouseRules: (state: RentalObjectState, payload: boolean) => {
    state.rentalObject.useDefaultHouseRules = payload;
  },
  setHouseRulesUploads: (state: RentalObjectState, payload: FileUpload[]) => {
    state.rentalObject.houseRulesUploads = payload;
  },
  setAdditionalDocuments: (state: RentalObjectState, payload: FileUpload[]) => {
    state.rentalObject.additionalDocuments = payload;
  },
};
